import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, ChevronDown, Search, Phone } from 'lucide-react';
import { trackPixelEvent } from '../utils/pixel';

import { LOGO_URL } from '../../constants';

import { StoreConfig, db, MenuItem, AdminCategory } from '../services/backend';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  config?: StoreConfig['topbar'];
  theme?: 'white' | 'dark' | 'auto';
  categories?: AdminCategory[];
}

const Header: React.FC<HeaderProps> = ({ config, theme = 'auto', categories = [] }) => {
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileAmbassadorOpen, setIsMobileAmbassadorOpen] = useState(false);
  const [navItems, setNavItems] = useState<MenuItem[]>([]);

  // Search State
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackPixelEvent('Search', { search_string: searchQuery });
      navigate(`/produtos?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const [isFixed, setIsFixed] = useState(false);

  const { cartCount } = useCart();

  useEffect(() => {
    const loadMenus = async () => {
      const menus = await db.getMenus();
      setNavItems(menus.sort((a, b) => a.order - b.order));
    };
    loadMenus();
  }, [location.pathname]);

  const showTopbar =
    config?.enabled && (location.pathname === '/' || location.pathname.startsWith('/produtos'));

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = showTopbar ? (window.innerWidth < 768 ? 36 : 40) : 0;

      setIsScrolled(scrollY > 20);
      setIsFixed(scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    // Call once to set initial state
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [config, location.pathname, showTopbar]);

  const handleNavClick = (path: string) => {
    let targetPath = path;
    if (!path.startsWith('http') && !path.startsWith('/')) {
      targetPath = `/${path}`;
    }

    if (path.startsWith('http')) {
      window.location.href = path;
    } else {
      navigate(targetPath);
    }

    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const headerPositionClass = showTopbar
    ? isFixed
      ? 'fixed top-0'
      : 'absolute top-[36px] md:top-[40px]'
    : 'fixed top-0';

  const darkHeroRoutes = ['/', '/embaixadora', '/cursos-ipog'];
  const hasDarkHero = darkHeroRoutes.includes(location.pathname);
  const isBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/');

  // Logic updated to respect theme prop
  const isTransparentOnDark =
    !isScrolled && !isMobileMenuOpen && (theme === 'white' || (theme === 'auto' && hasDarkHero));
  const forceDarkText = !isScrolled && !isMobileMenuOpen && theme === 'dark';

  const textColorClass = isTransparentOnDark
    ? 'text-white hover:text-alphamed-gold'
    : forceDarkText
      ? 'text-alphamed-brown hover:text-alphamed-gold'
      : 'text-alphamed-brown hover:text-alphamed-gold';
  const activeTextColorClass = isTransparentOnDark
    ? 'text-white font-semibold border-b border-white'
    : forceDarkText
      ? 'text-alphamed-gold font-semibold border-b border-alphamed-gold'
      : 'text-alphamed-gold font-semibold border-b border-alphamed-gold';

  const mobileMenuButtonClass = isTransparentOnDark ? 'text-white' : 'text-alphamed-brown';

  // Fundo branco forçado para /blog
  // The following variables are no longer directly used in the new 3-row header structure
  // const darkHeroRoutes = ['/', '/embaixadora', '/cursos-ipog'];
  // const hasDarkHero = darkHeroRoutes.includes(location.pathname);
  // const isBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  // const isTransparentOnDark = !isScrolled && !isMobileMenuOpen && (theme === 'white' || (theme === 'auto' && hasDarkHero));
  // const forceDarkText = !isScrolled && !isMobileMenuOpen && theme === 'dark';
  // const textColorClass = isTransparentOnDark ? 'text-white hover:text-alphamed-gold' : (forceDarkText ? 'text-alphamed-brown hover:text-alphamed-gold' : 'text-alphamed-brown hover:text-alphamed-gold');
  // const activeTextColorClass = isTransparentOnDark ? 'text-white font-semibold border-b border-white' : (forceDarkText ? 'text-alphamed-gold font-semibold border-b border-alphamed-gold' : 'text-alphamed-gold font-semibold border-b border-alphamed-gold');
  // const mobileMenuButtonClass = isTransparentOnDark ? 'text-white' : 'text-alphamed-brown';
  // const headerBgClass = isBlogPage ? 'bg-white shadow-sm border-alphamed-gold/20' : (isScrolled || isMobileMenuOpen ? 'bg-alphamed-cream/95 backdrop-blur-sm shadow-sm border-alphamed-gold/20' : 'bg-transparent border-transparent');

  // Separate menu items by type
  const topbarItems = navItems
    .filter((item) => item.type === 'topbar' || item.type === 'both')
    .sort((a, b) => a.order - b.order);
  const mainNavItems = navItems
    .filter((item) => (item.type === 'header' || item.type === 'both') && !item.parentId)
    .sort((a, b) => a.order - b.order);

  // Helpers to identify special menu items
  const isProdutosLink = (url: string) => url === '/produtos' || url.startsWith('/produtos');
  const isEMBAIXADORALink = (url: string) => url === '/embaixadora';
  const isMaisLink = (url: string) => false; // 'Mais' is likely handled dynamically or as a fallback

  return (
    <>
      {/* 1. TOP BAR (Institutional Links) */}
      {config?.enabled && (
        <div
          className="hidden md:block w-full relative z-[61]"
          style={{ backgroundColor: config.backgroundColor || '#f8f9fa' }}
        >
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center h-[32px] md:h-[40px] text-[10px] md:text-xs font-medium tracking-wide">
              {/* Left Side: Dynamic Links */}
              <div className="flex items-center gap-2 md:gap-6 overflow-x-auto scrollbar-hide py-1">
                {topbarItems.length > 0 ? (
                  topbarItems.map((link) => (
                    <Link
                      key={link.id}
                      to={link.url}
                      className="whitespace-nowrap hover:opacity-75 transition-opacity uppercase tracking-wider"
                      style={{ color: config.textColor || '#666' }}
                    >
                      {link.label}
                    </Link>
                  ))
                ) : (
                  /* Fallback Defaults */
                  <>
                    <Link
                      to="/sobre"
                      className="whitespace-nowrap hover:opacity-75"
                      style={{ color: config.textColor }}
                    >
                      QUEM SOMOS
                    </Link>
                    <Link
                      to="/contato"
                      className="whitespace-nowrap hover:opacity-75"
                      style={{ color: config.textColor }}
                    >
                      CONTATO
                    </Link>
                    <Link
                      to="/blog"
                      className="whitespace-nowrap hover:opacity-75"
                      style={{ color: config.textColor }}
                    >
                      BLOG
                    </Link>
                  </>
                )}
              </div>

              {/* Center: Marquee Text (if animated) or Static Text */}
              {config.text && (
                <div
                  className="hidden md:block flex-1 text-center px-4 overflow-hidden"
                  style={{ color: config.textColor }}
                >
                  {config.animated ? (
                    <div className="animate-marquee whitespace-nowrap inline-block">
                      <span className="mx-4">{config.text}</span>
                      <span className="mx-4">{config.text}</span>
                    </div>
                  ) : (
                    <span>{config.text}</span>
                  )}
                </div>
              )}

              {/* Right Link: Login/Register */}
              <div className="shrink-0 pl-4 border-l border-gray-200/20 ml-auto md:ml-0">
                <Link
                  to="/minha-conta"
                  className="hover:opacity-75 flex items-center gap-1"
                  style={{ color: config.textColor }}
                >
                  {user ? (
                    <span>
                      Olá, <span className="font-bold">{user.name.split(' ')[0]}</span>
                    </span>
                  ) : (
                    <span>
                      Olá, faça seu <span className="underline font-bold">login</span> ou{' '}
                      <span className="underline font-bold">cadastre-se</span>
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. MIDDLE BAR (Brand, Search, Contact, Cart) */}
      {/* Solid Background always */}
      <div className="w-full bg-alphamed-cream py-2 md:py-8 border-b-4 border-alphamed-gold/20 relative z-[60]">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Mobile Layout: Menu - Logo - Cart in single row */}
          <div className="flex md:hidden items-center justify-between gap-4">
            {/* Mobile Menu Toggle - Left */}
            <button
              className="p-2 text-alphamed-brown"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo - Center */}
            <div className="flex-1 flex justify-center">
              <Link to="/">
                <img
                  src={LOGO_URL}
                  alt="Alpha Estética - Equipamentos e Produtos de Estética"
                  className="w-[140px] h-auto object-contain"
                />
              </Link>
            </div>

            {/* Cart - Right */}
            <Link to="/carrinho" className="relative p-2">
              <ShoppingBag size={24} className="text-alphamed-brown" />
              <span className="absolute -top-1 -right-1 bg-alphamed-gold text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
                {cartCount}
              </span>
            </Link>
          </div>

          {/* Mobile Search Bar - Added as a new row below Logo/Cart */}
          <div className="md:hidden mt-3 w-full pb-1">
            <form onSubmit={handleSearch} className="relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="O que você procura?"
                className="w-full h-11 pl-4 pr-10 bg-white border border-gray-200 focus:border-alphamed-gold focus:outline-none transition-colors text-alphamed-brown placeholder-gray-400 font-sans text-sm rounded-xl shadow-sm"
              />
              <button
                type="submit"
                className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center text-alphamed-gold hover:text-alphamed-brown transition-colors"
              >
                <Search size={18} />
              </button>
            </form>
          </div>

          {/* Desktop Layout: Logo - Search - Actions */}
          <div className="hidden md:flex flex-row items-center gap-6 md:gap-12">
            {/* Logo */}
            <div className="shrink-0">
              <Link to="/">
                <img
                  src={LOGO_URL}
                  alt="Alpha Estética - Equipamentos e Produtos de Estética"
                  className="w-[180px] md:w-[220px] h-auto object-contain"
                />
              </Link>
            </div>

            {/* Search Bar - Wide & Center */}
            <div className="w-full flex-1 max-w-2xl relative group">
              <form onSubmit={handleSearch} className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="O que você procura?"
                  className="w-full h-12 pl-6 pr-14 bg-gray-50 border-b-2 border-gray-200 focus:border-alphamed-gold focus:outline-none transition-colors text-alphamed-brown placeholder-gray-400 font-sans text-sm md:text-base rounded-t-sm"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center text-alphamed-gold hover:text-alphamed-brown transition-colors"
                >
                  <Search size={22} />
                </button>
                {/* Underline highlight animation */}
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-alphamed-gold transition-all duration-300 group-focus-within:w-full"></div>
              </form>
            </div>

            {/* Right Actions: Televendas & Cart */}
            <div className="flex items-center gap-6 shrink-0">
              {/* Televendas - Hidden on small mobile to save space, visible on larger */}
              <div className="hidden lg:flex items-center gap-3 text-alphamed-brown">
                <div className="p-2 border border-alphamed-gold/30 rounded-full">
                  <Phone size={20} className="text-alphamed-gold" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                    Televendas
                  </span>
                  <span className="font-serif font-bold text-lg">+55 62 9409-2770</span>
                </div>
              </div>

              {/* Cart */}
              <Link to="/carrinho" className="flex items-center gap-3 group/cart">
                <div className="relative p-2">
                  <ShoppingBag
                    size={28}
                    className="text-alphamed-brown group-hover/cart:text-alphamed-gold transition-colors"
                  />
                  <span className="absolute -top-1 -right-1 bg-alphamed-gold text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
                    {cartCount}
                  </span>
                </div>
                <div className="flex flex-col leading-tight hidden sm:flex">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                    Meu Carrinho
                  </span>
                  <span className="font-serif font-bold text-sm text-alphamed-brown group-hover/cart:text-alphamed-gold transition-colors">
                    Ver itens
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 3. NAVIGATION BAR (Categories) */}
      <div
        className={`w-full bg-alphamed-cream border-b border-alphamed-gold/20 shadow-sm relative z-[59] hidden md:block ${isScrolled ? 'sticky top-0 shadow-md transform transition-transform duration-300' : ''}`}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <nav className="flex justify-between items-center h-14">
            <div className="flex items-center gap-8 lg:gap-12 w-full justify-center">
              {/* Dynamic Main Nav Items */}
              {mainNavItems.length > 0 ? (
                mainNavItems.map((item) => {
                  // Special Case: Produtos (Mega Menu)
                  if (item.url === '/produtos') {
                    return (
                      <div key={item.id} className="relative group h-full flex items-center">
                        <Link
                          to="/produtos"
                          className={`flex items-center font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 h-full border-b-2 ${location.pathname.startsWith('/produtos') ? 'text-alphamed-gold border-alphamed-gold' : 'text-alphamed-brown border-transparent hover:text-alphamed-gold hover:border-alphamed-gold/30'}`}
                        >
                          {item.label}
                          <ChevronDown
                            size={12}
                            className="ml-1 group-hover:rotate-180 transition-transform duration-300"
                          />
                        </Link>
                        {/* Mega Menu Content */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-[600px] lg:w-[800px] bg-white shadow-xl border-t-2 border-alphamed-gold opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-[110] rounded-b-lg overflow-hidden">
                          <div className="p-8 grid grid-cols-3 gap-x-8 gap-y-6">
                            {categories
                              .filter(
                                (cat: any) =>
                                  !cat.parentId &&
                                  cat.isActive !== false &&
                                  cat.name !== 'Equipamentos' &&
                                  !cat.name.includes('Kits') &&
                                  !cat.slug.includes('kits')
                              )
                              .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                              .map((cat) => (
                                <div key={cat.id} className="flex flex-col">
                                  <Link
                                    to={`/produtos/${cat.slug}`}
                                    className="font-serif text-alphamed-brown font-bold text-base hover:text-alphamed-gold transition-colors mb-2 block border-b border-gray-100 pb-1"
                                  >
                                    {cat.name}
                                  </Link>
                                  <div className="flex flex-col gap-1.5">
                                    {categories
                                      .filter(
                                        (child: any) =>
                                          child.parentId &&
                                          child.parentId === cat.id &&
                                          child.isActive !== false
                                      )
                                      .slice(0, 5)
                                      .map((child) => (
                                        <Link
                                          key={child.id}
                                          to={`/produtos/${child.slug}`}
                                          className="text-xs text-gray-500 hover:text-alphamed-gold transition-colors hover:translate-x-1 duration-200"
                                        >
                                          {child.name}
                                        </Link>
                                      ))}
                                  </div>
                                </div>
                              ))}
                            <div className="col-span-3 border-t border-gray-50 mt-4 pt-4 text-center">
                              <Link
                                to="/produtos"
                                className="inline-block px-8 py-2 border border-alphamed-gold text-alphamed-gold text-xs font-bold uppercase tracking-widest hover:bg-alphamed-gold hover:text-white transition-all rounded-full"
                              >
                                Ver Todos os Produtos
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Special Case: EMBAIXADORA (Dropdown)
                  if (item.url === '/embaixadora') {
                    return (
                      <div key={item.id} className="relative group h-full flex items-center">
                        <Link
                          to="/embaixadora"
                          className={`flex items-center font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 h-full border-b-2 ${location.pathname.startsWith('/embaixadora') || location.pathname.startsWith('/cursos') ? 'text-alphamed-gold border-alphamed-gold' : 'text-alphamed-brown border-transparent hover:text-alphamed-gold hover:border-alphamed-gold/30'}`}
                        >
                          {item.label}
                          <ChevronDown
                            size={12}
                            className="ml-1 group-hover:rotate-180 transition-transform duration-300"
                          />
                        </Link>
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-48 bg-white shadow-xl border-t-2 border-alphamed-gold opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-[110] rounded-b-lg overflow-hidden py-2">
                          <Link
                            to="/embaixadora"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block px-4 py-3 text-xs uppercase tracking-widest text-alphamed-brown hover:bg-gray-50 hover:text-alphamed-gold transition-colors"
                          >
                            MENTORIAS
                          </Link>
                          <Link
                            to="/cursos-ipog"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block px-4 py-3 text-xs uppercase tracking-widest text-alphamed-brown hover:bg-gray-50 hover:text-alphamed-gold transition-colors"
                          >
                            CURSOS
                          </Link>
                        </div>
                      </div>
                    );
                  }

                  // Special Case: "Mais" dropdown (if it's a dynamic item)
                  if (item.label.toUpperCase() === 'MAIS') {
                    return (
                      <div key={item.id} className="relative group h-full flex items-center">
                        <button
                          className={`flex items-center font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 h-full border-b-2 text-alphamed-brown border-transparent hover:text-alphamed-gold hover:border-alphamed-gold/30`}
                        >
                          {item.label}
                          <ChevronDown
                            size={12}
                            className="ml-1 group-hover:rotate-180 transition-transform duration-300"
                          />
                        </button>
                        <div className="absolute right-0 top-full w-48 bg-white shadow-xl border-t-2 border-alphamed-gold opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-[110] rounded-b-lg overflow-hidden py-2">
                          <Link
                            to="/blog"
                            className="block px-4 py-3 text-xs uppercase tracking-widest text-alphamed-brown hover:bg-gray-50 hover:text-alphamed-gold transition-colors"
                          >
                            BLOG
                          </Link>
                          {navItems
                            .filter((subItem) => subItem.parentId === item.id) // Filter children of 'Mais'
                            .map((subItem) => (
                              <Link
                                key={subItem.id}
                                to={subItem.url}
                                className="block px-4 py-3 text-xs uppercase tracking-widest text-alphamed-brown hover:bg-gray-50 hover:text-alphamed-gold transition-colors"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                        </div>
                      </div>
                    );
                  }

                  // Standard Link
                  const isOfertas = item.label.includes('OFERTAS');

                  return (
                    <Link
                      key={item.id}
                      to={item.url}
                      className={`font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-all duration-200 flex items-center gap-2
                                                ${
                                                  isOfertas
                                                    ? 'bg-fire-animated text-white px-5 py-2 rounded-full hover:brightness-110 shadow-sm mx-1'
                                                    : `py-4 border-b-2 ${location.pathname === item.url ? 'text-alphamed-gold border-alphamed-gold' : 'text-alphamed-brown border-transparent hover:text-alphamed-gold hover:border-alphamed-gold/30'}`
                                                }`}
                    >
                      {isOfertas && (
                        <img
                          src="https://designagora.onlineaprender.com/wp-content/uploads/2025/09/gif-fogo.gif"
                          alt="Fogo"
                          className="w-4 h-4 object-contain brightness-0 invert"
                        />
                      )}
                      {item.label}
                    </Link>
                  );
                })
              ) : (
                /* Fallback: Hardcoded Default Menu if DB is empty */
                <>
                  <Link
                    to="/"
                    className={`font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 py-4 border-b-2 ${location.pathname === '/' ? 'text-alphamed-gold border-alphamed-gold' : 'text-alphamed-brown border-transparent hover:text-alphamed-gold hover:border-alphamed-gold/30'}`}
                  >
                    INÍCIO
                  </Link>
                  {/* Products Fallback */}
                  <div className="relative group h-full flex items-center">
                    <Link
                      to="/produtos"
                      className={`flex items-center font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 h-full border-b-2 ${location.pathname.startsWith('/produtos') ? 'text-alphamed-gold border-alphamed-gold' : 'text-alphamed-brown border-transparent hover:text-alphamed-gold hover:border-alphamed-gold/30'}`}
                    >
                      PRODUTOS <ChevronDown size={12} className="ml-1" />
                    </Link>
                    {/* MegaMenu would go here logic reuse needed if we want it perfect in fallback, or just simple link */}
                  </div>
                  <Link
                    to="/kits"
                    className="font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 py-4 border-b-2 text-alphamed-brown border-transparent hover:text-alphamed-gold"
                  >
                    KITS
                  </Link>
                  <Link
                    to="/equipamentos"
                    className="font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 py-4 border-b-2 text-alphamed-brown border-transparent hover:text-alphamed-gold"
                  >
                    EQUIPAMENTOS
                  </Link>
                  <Link
                    to="/embaixadora"
                    className="font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 py-4 border-b-2 text-alphamed-brown border-transparent hover:text-alphamed-gold"
                  >
                    EMBAIXADORA
                  </Link>
                  <Link
                    to="/blog"
                    className="font-sans text-xs lg:text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 py-4 border-b-2 text-alphamed-brown border-transparent hover:text-alphamed-gold"
                  >
                    BLOG
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Menu (Overlay) - Updated to use navItems */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[200] bg-alphamed-cream overflow-y-auto">
          <div className="p-4 flex justify-between items-center border-b border-alphamed-gold/20">
            <img src={LOGO_URL} alt="Alpha Estética - Menu Mobile" className="h-10 w-auto" />
            <button className="p-2 text-alphamed-brown" onClick={() => setIsMobileMenuOpen(false)}>
              <X size={28} />
            </button>
          </div>
          <div className="py-4">
            {mainNavItems.length > 0 ? (
              mainNavItems.map((item) => {
                if (item.url === '/embaixadora') {
                  return (
                    <div key={item.id} className="border-b border-gray-100">
                      <button
                        onClick={() => setIsMobileAmbassadorOpen(!isMobileAmbassadorOpen)}
                        className="w-full text-left px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest flex justify-between items-center"
                      >
                        {item.label}{' '}
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${isMobileAmbassadorOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isMobileAmbassadorOpen && (
                        <div className="bg-gray-50 py-2">
                          <Link
                            to="/embaixadora"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block px-10 py-3 text-sm text-gray-600 uppercase tracking-widest"
                          >
                            MENTORIAS
                          </Link>
                          <Link
                            to="/cursos-ipog"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block px-10 py-3 text-sm text-gray-600 uppercase tracking-widest"
                          >
                            CURSOS
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                }
                const isOfertas = item.label.includes('OFERTAS');

                return (
                  <Link
                    key={item.id}
                    to={item.url}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                    className={`px-6 py-4 font-bold uppercase tracking-widest border-b border-gray-100 transition-colors flex items-center gap-3
                                            ${
                                              isOfertas
                                                ? 'bg-fire-animated text-white'
                                                : `text-alphamed-brown ${location.pathname === item.url ? 'text-alphamed-gold' : ''}`
                                            }`}
                  >
                    {isOfertas && (
                      <img
                        src="https://designagora.onlineaprender.com/wp-content/uploads/2025/09/gif-fogo.gif"
                        alt="Fogo"
                        className="w-5 h-5 object-contain brightness-0 invert"
                      />
                    )}
                    {item.label}
                  </Link>
                );
              })
            ) : (
              <>
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest border-b border-gray-100"
                >
                  Início
                </Link>
                <Link
                  to="/produtos"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest border-b border-gray-100"
                >
                  Produtos
                </Link>
                <Link
                  to="/kits"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest border-b border-gray-100"
                >
                  Kits
                </Link>
                <Link
                  to="/equipamentos"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest border-b border-gray-100"
                >
                  Equipamentos
                </Link>
                <div className="border-b border-gray-100">
                  <button
                    onClick={() => setIsMobileAmbassadorOpen(!isMobileAmbassadorOpen)}
                    className="w-full text-left px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest flex justify-between items-center"
                  >
                    EMBAIXADORA{' '}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${isMobileAmbassadorOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isMobileAmbassadorOpen && (
                    <div className="bg-gray-50 py-2">
                      <Link
                        to="/embaixadora"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-10 py-3 text-sm text-gray-600 uppercase tracking-widest"
                      >
                        Mentorias
                      </Link>
                      <Link
                        to="/cursos-ipog"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-10 py-3 text-sm text-gray-600 uppercase tracking-widest"
                      >
                        Cursos
                      </Link>
                    </div>
                  )}
                </div>
                <Link
                  to="/blog"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest border-b border-gray-100"
                >
                  Blog
                </Link>
                <Link
                  to="/contato"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-4 text-alphamed-brown font-bold uppercase tracking-widest border-b border-gray-100"
                >
                  Contato
                </Link>
              </>
            )}
            {/* Topbar links in mobile menu as well */}
            <div className="bg-gray-50 mt-4 py-2">
              {topbarItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.url}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-6 py-3 text-xs text-gray-500 uppercase tracking-widest"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
